import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface AdminOrder {
  id: string;
  order_number: string;
  status: string;
  final_price: number | null;
  proposed_price: number | null;
  pickup_date: string;
  delivery_date: string;
  created_at: string;
  shipper_company: {
    id: string;
    name: string;
  } | null;
  transport_jobs: Array<{
    transporter_company: {
      id: string;
      name: string;
    } | null;
  }>;
}

export interface AdminOrdersFilters {
  status?: string;
  shipper_company_id?: string;
  transporter_company_id?: string;
  date_from?: string;
  date_to?: string;
}

export const useAdminOrders = (filters?: AdminOrdersFilters) => {
  return useQuery({
    queryKey: ['admin-orders', filters],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        .from('orders')
        .select(`
          *,
          shipper_company:companies!shipper_company_id(id, name),
          transport_jobs(
            transporter_company:companies!transporter_company_id(id, name)
          )
        `)
        .order('created_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status as any);
      }

      if (filters?.shipper_company_id) {
        query = query.eq('shipper_company_id', filters.shipper_company_id);
      }

      if (filters?.date_from) {
        query = query.gte('created_at', filters.date_from);
      }

      if (filters?.date_to) {
        query = query.lte('created_at', filters.date_to);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Filter by transporter if specified
      let filteredData = data as AdminOrder[];
      if (filters?.transporter_company_id) {
        filteredData = filteredData.filter(order =>
          order.transport_jobs.some(
            job => job.transporter_company?.id === filters.transporter_company_id
          )
        );
      }

      return filteredData;
    },
  });
};

export const useMarkOrderDelivered = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderId: string) => {
      const { error } = await supabase
        .from('orders')
        .update({ status: 'delivered' })
        .eq('id', orderId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
      toast.success('Order marked as delivered');
    },
    onError: (error) => {
      console.error('Error marking order as delivered:', error);
      toast.error('Error marking order as delivered');
    },
  });
};

export const useCancelOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderId: string) => {
      const { error } = await supabase
        .from('orders')
        .update({ status: 'cancelled' })
        .eq('id', orderId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
      toast.success('Order cancelled');
    },
    onError: (error) => {
      console.error('Error cancelling order:', error);
      toast.error('Error cancelling order');
    },
  });
};
